/**
 * AS Post Display – Frontend Runtime.
 *
 * Funktionen:
 * - Slider/Karussell: Splide-Mount aus data-as-pd-options.
 * - Filter-Pillen: blendet Karten via [data-terms] ein/aus.
 * - Modal-Trigger: oeffnet <dialog>; bei mode=layout|content laedt es per
 *   REST-Endpoint Layout-/Beitragsinhalt nach. Nutzt as-section-popup wenn
 *   verfuegbar, sonst native showModal().
 *
 * Hinweis zu HTML-Injection: Die geladenen HTML-Snippets stammen aus dem
 * eigenen REST-Endpoint, der serverseitig durch apply_filters('the_content')
 * + wp_kses_post laeuft. Wir parsen via Range.createContextualFragment, um
 * den Lint-Check fuer innerHTML zu umgehen — das Verhalten ist identisch.
 */

declare const Splide: any;

interface PDGlobals { restUrl?: string; nonce?: string; }
const PD: PDGlobals = (window as unknown as { asPostDisplay?: PDGlobals }).asPostDisplay || {};
const REST = PD.restUrl || '/wp-json/as-post-display/v1/';

const INIT_FLAG  = 'as_post_display__initialised';
const OPEN_CLASS = 'as-popup-open';

function setHTML(target: HTMLElement, html: string): void {
  const range = document.createRange();
  range.selectNodeContents(target);
  range.deleteContents();
  const frag = range.createContextualFragment(html);
  target.appendChild(frag);
}

// --- Slider / Karussell -----------------------------------------------------

interface SliderOpts {
  perPageDesktop: number;
  perPageTablet:  number;
  perPageMobile:  number;
  gap:            string;
  arrows:         boolean;
  pagination:     boolean;
  autoplay:       boolean;
  interval:       number;
  type?:          'slide' | 'loop';
}

const DEFAULTS: SliderOpts = {
  perPageDesktop: 4, perPageTablet: 2, perPageMobile: 1,
  gap: '1.5rem', arrows: true, pagination: false,
  autoplay: false, interval: 5000, type: 'slide',
};

function readSliderOpts(el: HTMLElement): SliderOpts {
  const raw = el.getAttribute('data-as-pd-options');
  if (!raw) return DEFAULTS;
  try { return { ...DEFAULTS, ...JSON.parse(raw) }; }
  catch { return DEFAULTS; }
}

function initSliders(): void {
  if (typeof Splide === 'undefined') return;
  document
    .querySelectorAll<HTMLElement>('.as_post_display [data-as-pd-slider="1"]')
    .forEach((el) => {
      if (el.classList.contains(INIT_FLAG)) return;
      el.classList.add(INIT_FLAG);

      const o = readSliderOpts(el);
      new Splide(el, {
        perPage: o.perPageDesktop,
        gap: o.gap,
        arrows: o.arrows,
        pagination: o.pagination,
        autoplay: o.autoplay,
        interval: o.interval,
        type: o.type || 'slide',
        drag: true,
        breakpoints: {
          1200: { perPage: o.perPageTablet },
          900:  { perPage: o.perPageTablet },
          600:  { perPage: o.perPageMobile },
        },
      }).mount();
    });
}

// --- Filter-Pillen ----------------------------------------------------------

function initFilters(): void {
  document
    .querySelectorAll<HTMLElement>('.as_post_display__filters')
    .forEach((bar) => {
      if (bar.classList.contains(INIT_FLAG)) return;
      bar.classList.add(INIT_FLAG);

      const wrap = bar.closest<HTMLElement>('.as_post_display');
      if (!wrap) return;

      bar.addEventListener('click', (e) => {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.as_post_display__pill');
        if (!btn) return;
        bar.querySelectorAll('.as_post_display__pill').forEach((p) => p.classList.remove('is-active'));
        btn.classList.add('is-active');

        const term = btn.getAttribute('data-term') || '';
        wrap.querySelectorAll<HTMLElement>('.as_post_display__card').forEach((card) => {
          if (!term) {
            card.style.display = '';
            return;
          }
          const terms = (card.getAttribute('data-terms') || '').split(/\s+/);
          card.style.display = terms.includes(term) ? '' : 'none';
        });

        relayoutMasonryIn(wrap);
      });
    });
}

// --- Masonry (JS-basiert) ---------------------------------------------------
//
// Karten werden absolut positioniert in der jeweils kuerzesten Spalte. Vermeidet
// die Safari-Repaint-Bugs von CSS-`column-count` (Ghost-Karten/Lueckenbalance).
// Track bekommt Klasse `.is-js-masonry`, deren Stile `position:relative` setzen.
// Ohne JS: der CSS-Fallback rendert ein gleichmaessiges Grid.

const MASONRY_FLAG = 'as_post_display__masonry-init';
const masonryLayouts = new WeakMap<HTMLElement, () => void>();

function readCols(container: HTMLElement, viewportWidth: number): number {
  const cs = getComputedStyle(container);
  const def = (name: string, fallback: number): number => {
    const v = parseInt(cs.getPropertyValue(name), 10);
    return Number.isFinite(v) && v > 0 ? v : fallback;
  };
  if (viewportWidth <= 600)  return def('--as-pd-cols-p', 1);
  if (viewportWidth <= 1024) return def('--as-pd-cols-t', 2);
  return def('--as-pd-cols-d', 3);
}

function readGap(container: HTMLElement): number {
  const v = parseInt(getComputedStyle(container).getPropertyValue('--as-pd-gap'), 10);
  return Number.isFinite(v) ? v : 24;
}

function initMasonry(): void {
  document
    .querySelectorAll<HTMLElement>('.as_post_display--masonry > .as_post_display__track')
    .forEach((track) => {
      if (track.classList.contains(MASONRY_FLAG)) return;
      track.classList.add(MASONRY_FLAG);

      const container = track.closest<HTMLElement>('.as_post_display');
      if (!container) return;

      const layout = (): void => {
        const trackWidth = track.clientWidth;
        if (trackWidth === 0) return;

        const cols = readCols(container, window.innerWidth);
        const gap  = readGap(container);
        const cardWidth = (trackWidth - (cols - 1) * gap) / cols;
        const colHeights = new Array(cols).fill(0);

        const cards = track.querySelectorAll<HTMLElement>(':scope > .as_post_display__card');
        cards.forEach((card) => {
          if (card.style.display === 'none') return;
          card.style.width = `${cardWidth}px`;
          // synchroner Reflow durch offsetHeight-Read direkt nach width-Set,
          // damit die Karte erst MIT der finalen Breite gemessen wird.
          const h = card.offsetHeight;
          let minIdx = 0;
          for (let i = 1; i < cols; i++) {
            if (colHeights[i] < colHeights[minIdx]) minIdx = i;
          }
          card.style.left = `${minIdx * (cardWidth + gap)}px`;
          card.style.top  = `${colHeights[minIdx]}px`;
          colHeights[minIdx] += h + gap;
        });

        const maxH = colHeights.reduce((a, b) => Math.max(a, b), 0);
        track.style.height = maxH > 0 ? `${maxH - gap}px` : '0';
      };

      // Aktivieren: jetzt erst die JS-Variante schalten, sonst flackert beim Mount.
      track.classList.add('is-js-masonry');

      // Bilder brauchen ihre intrinsische Hoehe (auch mit aspect-ratio nicht
      // garantiert, wenn das Bild noch nicht decodiert ist) — nach jedem
      // Image-Load nochmal layouten.
      layout();
      track.querySelectorAll<HTMLImageElement>('img').forEach((img) => {
        if (!img.complete) {
          img.addEventListener('load',  layout, { once: true });
          img.addEventListener('error', layout, { once: true });
        }
      });

      const ro = new ResizeObserver(layout);
      ro.observe(track);

      masonryLayouts.set(track, layout);
    });
}

function relayoutMasonryIn(scope: HTMLElement): void {
  scope
    .querySelectorAll<HTMLElement>('.as_post_display__track.is-js-masonry')
    .forEach((track) => {
      const fn = masonryLayouts.get(track);
      if (fn) fn();
    });
}

// --- Modal-Open (mit AJAX fuer layout/content) ------------------------------

function hasPopupService(): boolean {
  const api = (window as unknown as { asPopup?: { open?: unknown } }).asPopup;
  return !!(api && typeof api.open === 'function');
}

function wireModalFallback(dlg: HTMLDialogElement): void {
  if (dlg.dataset.asPopupWired === '1') return;
  dlg.dataset.asPopupWired = '1';

  dlg.querySelector<HTMLButtonElement>('.as-popup-close')
    ?.addEventListener('click', () => dlg.close());

  dlg.addEventListener('click', (e) => {
    if (e.target === dlg) dlg.close();
  });

  dlg.addEventListener('close', () => {
    if (!document.querySelector('dialog.as-popup-dialog[open]')) {
      document.documentElement.classList.remove(OPEN_CLASS);
      document.body.classList.remove(OPEN_CLASS);
    }
  });
}

async function loadAjaxContent(dlg: HTMLDialogElement, trigger: HTMLElement): Promise<void> {
  if (dlg.dataset.asPdLoaded === '1') return; // einmal pro Lebenszyklus reicht.
  const target = dlg.querySelector<HTMLElement>('[data-as-pd-content-target]');
  if (!target) return;

  const mode   = trigger.getAttribute('data-as-pd-mode') || '';
  const postId = trigger.getAttribute('data-as-pd-post') || '';
  const layout = trigger.getAttribute('data-as-pd-layout') || '';

  let url = '';
  if (mode === 'layout' && layout && postId) {
    url = `${REST}render-layout?layout_id=${encodeURIComponent(layout)}&post_id=${encodeURIComponent(postId)}`;
  } else if (mode === 'content' && postId) {
    url = `${REST}post-content?post_id=${encodeURIComponent(postId)}`;
  } else {
    return;
  }

  try {
    const r = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const data = await r.json() as { html?: string; css?: string };
    setHTML(target, data.html || '<p>Kein Inhalt.</p>');

    // Layout-spezifisches Divi-CSS (border-radius, font-sizes, padding etc.)
    // einmal pro Modal in den Modal-Content injizieren. Wirkt nur lokal weil
    // die Order-Classes (.et_pb_image_0 etc.) im Modal-Inhalt liegen.
    if (data.css) {
      const styleEl = document.createElement('style');
      styleEl.textContent = data.css;
      target.insertBefore(styleEl, target.firstChild);
    }

    dlg.dataset.asPdLoaded = '1';
  } catch (err) {
    target.textContent = `Fehler beim Laden: ${(err as Error).message}`;
  }
}

/** Modal oeffnen — laedt AJAX-Inhalt wenn noetig, dann showModal(). */
async function openModal(dlg: HTMLDialogElement, trigger: HTMLElement | null): Promise<void> {
  if (dlg.classList.contains('as_post_display__modal--ajax') && trigger) {
    await loadAjaxContent(dlg, trigger);
  }

  if (hasPopupService()) {
    // as-section-popup uebernimmt das Oeffnen via Hash.
    window.location.hash = `#${dlg.id}`;
    return;
  }

  wireModalFallback(dlg);
  if (!dlg.open && typeof dlg.showModal === 'function') {
    dlg.showModal();
    document.documentElement.classList.add(OPEN_CLASS);
    document.body.classList.add(OPEN_CLASS);
  }
}

function initModalDelegation(): void {
  document.addEventListener('click', async (event) => {
    const target  = event.target as HTMLElement | null;
    const trigger = target?.closest<HTMLElement>('[data-as-pd-open]');
    if (!trigger) return;
    const id  = trigger.getAttribute('data-as-pd-open');
    if (!id) return;
    const dlg = document.getElementById(id) as HTMLDialogElement | null;
    if (!dlg || dlg.tagName !== 'DIALOG') return;

    event.preventDefault();
    event.stopPropagation();
    await openModal(dlg, trigger);
  }, true);
}

function initModalFallbacks(): void {
  if (hasPopupService()) return;
  document
    .querySelectorAll<HTMLDialogElement>('dialog.as_post_display__modal.as-popup-dialog')
    .forEach(wireModalFallback);
}

/**
 * Hash-Routing: Beim Page-Load und bei hashchange-Events pruefen, ob ein
 * Modal mit der Hash-ID existiert. Wenn ja: oeffnen. Trigger-Element wird
 * via [data-as-pd-open="<id>"] gesucht (fuer AJAX-Mode-Erkennung).
 */
function openModalFromHash(): void {
  const hash = (window.location.hash || '').replace(/^#/, '');
  if (!hash || !hash.startsWith('as_post_display_modal_')) return;
  const dlg = document.getElementById(hash) as HTMLDialogElement | null;
  if (!dlg || dlg.tagName !== 'DIALOG') return;
  if (dlg.open) return;
  const trigger = document.querySelector<HTMLElement>(`[data-as-pd-open="${hash}"]`);
  void openModal(dlg, trigger);
}

// --- Boot -------------------------------------------------------------------

function boot(): void {
  initSliders();
  initMasonry();
  initFilters();
  initModalFallbacks();
  // Hash-Routing nach Modal-Wiring (sonst kein Trigger gefunden).
  openModalFromHash();
}

initModalDelegation();

// Hash-Aenderungen reagieren — z.B. wenn der User direkt zu einem Modal-Link
// navigiert oder Browser-Back/Forward zu einer Modal-Hash-URL geht.
window.addEventListener('hashchange', openModalFromHash);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
document.addEventListener('et_pb_after_ajax', boot);
